// /asset-groups wire schemas.
//
// AssetGroup is wholly engine-scoped, so request/response bodies are the
// engine schemas verbatim. Path params just need a UUID validator.
//
// Sesión 4 Tanda D introduces a richer cartera response (`assetGroupListItem`)
// the FE renders via the `GroupCard` pattern: thumb + title + subline +
// strip cifra. The wire-format keys are recipe-agnostic — the server
// computes `subline` and `stats[]` from the active recipe before sending
// (see core-api/src/routes/asset-groups.ts).
import { assetGroupCreateSchema, assetGroupIdSchema, assetGroupSchema, assetGroupUpdateSchema, assetSchema, organizationIdSchema, reportIdSchema, reportStatusSchema, userIdSchema, } from "@bitacorapro/core-engine";
import { isoDateTimeSchema, slugSchema } from "@bitacorapro/shared";
import { z } from "zod";
import { brandSummarySchema } from "./brands.js";
import { paginated } from "./common.js";
// The DB-resident report status, derived from the engine's own
// reportStatusSchema rather than hand-copied. This exact drift (a
// hand-rolled wire-level enum silently falling behind the engine's) is
// what made in-flight reports vanish from /cartera's resume card twice
// (#0006, #0474) — see core-engine/src/enums.ts's comment for the
// incident history. Deriving instead of copying means a new engine
// status (e.g. ADR-0098's "closed") propagates here automatically.
const wireReportStatus = reportStatusSchema;
export const assetGroupParams = z.object({ id: assetGroupIdSchema });
// `organizationId` is taken from the authenticated context; clients don't
// pass it. Strip it from the create body so a client can't spoof it.
// `kind`, `supervisorUserId`, `addressLine` and `attributes` arrive from
// the client; the route then validates `kind` against the active recipe.
export const assetGroupCreateBody = assetGroupCreateSchema.omit({ organizationId: true }).extend({
    kind: slugSchema.optional(),
    supervisorUserId: userIdSchema.optional(),
    addressLine: z.string().trim().max(500).optional(),
    attributes: z.record(z.string(), z.unknown()).optional(),
});
export const assetGroupUpdateBody = assetGroupUpdateSchema.extend({
    kind: slugSchema.optional(),
    // Pass `null` to clear the supervisor; omit the key to leave it
    // untouched.
    supervisorUserId: userIdSchema.nullable().optional(),
    // ADR-0124 Decisión 10 — `asset_groups.supervisor_user_id` holds at
    // most ONE holder per group and is the scope projection of every
    // `scopeType: "asset_group"` role (postventa's `jefe_proyecto`,
    // facility's `facility_supervisor`). Overwriting a live holder with a
    // different one silently de-scopes the incumbent, so the route answers
    // 409 ASSET_GROUP_SUPERVISOR_OCCUPIED unless the caller opts in here.
    //
    // A wire flag rather than clear-then-set (the two mechanisms the
    // council left open): succession is atomic — the project never passes
    // through a window with no holder — and one audit row carries previous
    // and next holder together instead of two rows a reader has to join.
    // Meaningless without `supervisorUserId`; the route ignores it there.
    replaceSupervisor: z.boolean().optional(),
    addressLine: z.string().trim().max(500).nullable().optional(),
    attributes: z.record(z.string(), z.unknown()).optional(),
});
// Single stat shown on the GroupCard's strip cifra ("12 propiedades").
// Today there's always exactly one stat per recipe; the array keeps the
// door open for adding more (Patrones_Sesion4 documents the dot-separated
// variant) without a schema change.
export const groupStatSchema = z.object({
    n: z.number().int().nonnegative(),
    label: z.string().min(1),
});
// Wire shape for /cartera's GroupCard. Recipe-agnostic: `kind` carries
// the slug ("proyecto" / "zona"), the FE picks the glifo / thumb; the
// server resolves `subline` (address | supervisor displayName | absent)
// and `stats[]` from the recipe and the DB.
export const assetGroupListItemSchema = z.object({
    id: assetGroupIdSchema,
    name: z.string().min(1).max(255),
    kind: slugSchema.optional(),
    // Omitted entirely when `recipe.group.sublineSource === "none"`.
    subline: z.string().optional(),
    stats: z.array(groupStatSchema),
    createdAt: isoDateTimeSchema,
});
export const assetGroupListResponseV2 = z.object({
    items: z.array(assetGroupListItemSchema),
});
// Shared "latest report" summary shape — mirrors what /assets uses (see
// assets.ts). Exported so other wire modules (e.g. cartera-dashboard.ts)
// can reuse it instead of re-declaring the shape.
export const latestReportSummarySchema = z.object({
    id: reportIdSchema,
    reportType: slugSchema,
    status: wireReportStatus,
    createdAt: isoDateTimeSchema,
    signedAt: isoDateTimeSchema.nullable().optional(),
    // ADR-0098 — the terminal timestamp for a report closed by unilateral
    // attestation (facility_management). Without this, a consumer has no
    // way to tell an attestation-closed report from an in-progress one:
    // `signedAt` stays null for both. Read via reportTerminalAt() from
    // @bitacorapro/core-engine, never `signedAt` alone.
    closedAt: isoDateTimeSchema.nullable().optional(),
});
// /asset-groups/:id detail. The asset list piggy-backs so the FE renders
// /cartera/:groupId in a single round-trip.
export const assetGroupAssetItemSchema = assetSchema.extend({
    latestReport: latestReportSummarySchema.nullable(),
    // ADR-0023 §Consequences — denorm of max severity rank across
    // findings of the asset's latest signed report. Null when the asset
    // has no signed reports. Mirrors the same field on assetListItemSchema.
    latestVisitSeverity: z.string().nullable().optional(),
    // Effective brand for this asset (ADR-0033 §C1). Null for verticals
    // without brands or when no brand row is associated.
    brand: brandSummarySchema.nullable().optional(),
});
export const assetGroupDetailResponse = z.object({
    group: assetGroupListItemSchema.extend({
        organizationId: organizationIdSchema,
        description: z.string().optional(),
        attributes: z.record(z.string(), z.unknown()),
        // Brand at the group level (`asset_groups.brand_id` → brands).
        // Null for verticals without brands (arriendos, postventa) or when
        // the group has no brand association.
        brand: brandSummarySchema.nullable(),
    }),
    assets: z.object({
        items: z.array(assetGroupAssetItemSchema),
        nextCursor: z.string().nullable(),
    }),
});
export const assetGroupResponse = assetGroupSchema;
// Legacy paginated response kept for back-compat with callers that
// still walk the v1 shape (engine row verbatim). New consumers use
// `assetGroupListResponseV2`.
export const assetGroupListResponse = paginated(assetGroupSchema);
//# sourceMappingURL=asset-groups.js.map